import { Fragment, useContext, useState } from "react";
import { grey } from "@mui/material/colors";

import { StyledButton } from "../components";
import { LangContext } from "../contexts/langContext";
import { SelectAnswerContext } from "../contexts/selectAnswerContext";
import useShowingCorrectAnswer from "../hooks/useShowingCorrectAnswer";
import { green, red } from "../config/index";
import { CircleOrCrossOutStatements, CorrectOrIncorrect, TranslationData } from "@common";

interface RenderProps extends CircleOrCrossOutStatements {
  readOnly?: boolean;
  showCorrect?: boolean;
}

type StatementId = "1" | "2" | "3" | "4" | "5" | "6";

const CircleOrCrossOut: React.FC<{
  [key in keyof RenderProps]: RenderProps[key];
}> = ({
  mode,
  statement1Text,
  statement1IsCorrect,
  statement2Text,
  statement2IsCorrect,
  statement3Text,
  statement3IsCorrect,
  statement4Text,
  statement4IsCorrect,
  statement5Text,
  statement5IsCorrect,
  statement6Text,
  statement6IsCorrect,
  readOnly = false,
  showCorrect = false,
}) => {
  const { translateTranslationData, translatePageText } = useContext(LangContext);
  const { onSelectAnswer } = useContext(SelectAnswerContext);
  const showingCorrectAnswer = useShowingCorrectAnswer();
  const displayCorrectAnswers = showCorrect || showingCorrectAnswer;

  const [selectedStatements, setSelectedStatements] = useState<Array<string>>([]);
  const [statementData] = useState<
    Record<string, { isCorrect: CorrectOrIncorrect; text: TranslationData }>
  >({
    "1": { isCorrect: statement1IsCorrect, text: statement1Text },
    "2": { isCorrect: statement2IsCorrect, text: statement2Text },
    "3": { isCorrect: statement3IsCorrect, text: statement3Text },
    "4": { isCorrect: statement4IsCorrect, text: statement4Text },
    "5": { isCorrect: statement5IsCorrect, text: statement5Text },
    "6": { isCorrect: statement6IsCorrect, text: statement6Text },
  });

  const toggleSelected = (toggleId: string) => {
    const oldSelectedStatements = [...selectedStatements];
    const index = oldSelectedStatements.indexOf(toggleId);

    if (index === -1) {
      oldSelectedStatements.push(toggleId);
    } else {
      oldSelectedStatements.splice(index, 1);
    }

    setSelectedStatements(oldSelectedStatements);
  };

  const checkAnswer = () => {
    let allCorrect = true;
    for (const id in statementData) {
      const data = statementData[id as StatementId];

      if (data.isCorrect === "correct" && selectedStatements.includes(id) === false) {
        allCorrect = false;
      }
      if (data.isCorrect === "incorrect" && selectedStatements.includes(id) === true) {
        allCorrect = false;
      }
    }

    const answerStatValues = {
      val1: 0,
      val2: allCorrect ? 1 : 0,
      val3: allCorrect ? 0 : 1,
    };
    onSelectAnswer(allCorrect ? "correct" : "incorrect", answerStatValues);
  };

  return (
    <>
      {Object.keys(statementData).map((id, index: number) => {
        const td: TranslationData = statementData[id as StatementId]["text"];
        const correctOrIncorrect: CorrectOrIncorrect = statementData[id].isCorrect;
        return (
          <Statement
            key={index}
            index={index}
            toggleSelected={toggleSelected}
            selectedStatements={selectedStatements}
            id={id}
            text={translateTranslationData(td)}
            readOnly={readOnly || false}
            displayCorrectAnswers={displayCorrectAnswers}
            isCorrect={correctOrIncorrect === "correct"}
          />
        );
      })}
      <StyledButton
        hasCooldown
        fullWidth
        style={{ marginTop: 16 }}
        onClick={() => checkAnswer()}
        label={translatePageText("test.checkCorrect")}
        disabled={readOnly || false}
      />
    </>
  );
};

const Statement: React.FC<{
  toggleSelected: (id: string) => void;
  text: string;
  id: string;
  index: number;
  selectedStatements: Array<string>;
  readOnly: boolean;
  displayCorrectAnswers: boolean;
  isCorrect: boolean;
}> = ({
  toggleSelected,
  text,
  id,
  selectedStatements,
  index,
  readOnly,
  displayCorrectAnswers,
  isCorrect,
}) => {
  const selected = selectedStatements.includes(id);

  if (text === "") return <Fragment key={index}></Fragment>;

  return (
    <StyledButton
      key={index}
      fullWidth
      onClick={() => toggleSelected(id)}
      style={{
        margin: "4px 0",
        background:
          displayCorrectAnswers && isCorrect
            ? green[100]
            : displayCorrectAnswers && !isCorrect
            ? red[200]
            : selected
            ? green[100]
            : "rgba(255,255,255,0.7)",
        fontWeight: "normal",
        filter: "none",
        textAlign: "center",
        border:
          displayCorrectAnswers && isCorrect
            ? "solid 3px " + green[500]
            : displayCorrectAnswers && !isCorrect
            ? "solid 3px " + red[500]
            : selected
            ? "solid 3px " + green[500]
            : "solid 3px rgba(0,0,0,0)",
        color: (displayCorrectAnswers && isCorrect) || selected ? green[700] : grey[800],
      }}
      label={text}
      disabled={readOnly}
    />
  );
};

export default CircleOrCrossOut;
